import { EntitlementKeys, grantCovers, grantCoversPerson, isEntitledAction, isPackageDenied, SiteEntitlementsProvider } from '@weavix/domain/src/people/permission-rules';
import { PermissionApiResponse, PermissionAction, PermissionApiResponseItem } from '@weavix/models/src/permission/permissions.model';
import { PersonFacilityPredicate, PersonLike } from '@weavix/models/src/person/person-matcher';
import { memoize } from 'lodash';
import { isPackageEntitled } from '@weavix/domain/src/packaging/package-gated-actions';

/**
 * WARNING: this class has a similar implementation in @weavix/domain/src/people/permission-checker.ts
 */
export class PermissionChecker {

    /**
     * @param permissions the account's permissions from the server.
     * @param siteEntitlements resolves site entitlements; without it, or when it returns null, `enabledActions` decides as before.
     */
    constructor(private readonly permissions: PermissionApiResponse, private readonly siteEntitlements?: SiteEntitlementsProvider) {
        this.entitlementKeys = new EntitlementKeys(siteEntitlements, () => this.hasPersonPermissionMemo.cache.clear?.());
    }

    private readonly entitlementKeys: EntitlementKeys;

    /**
     * Returns `true` if there is:
     * * an account permission
     * * facility specified and a facility permission matching
     * * folder specified and a folder permission matching
     *
     * With `entitledAccountWide`, entitlement is checked across every package in the account rather than at `facilityId`,
     * as the server does for something that applies to all sites.
     */
    public hasPermission(action: PermissionAction, facilityId?: string, folderId?: string, entitledAccountWide = false): boolean {
        return this.permissions.grants
            .filter(perm => this.isPermissionInScope(perm, facilityId, folderId))
            .some(perm => this.isPermissionGranted(perm, action, entitledAccountWide ? undefined : facilityId));
    }

    // returns true if there is:
    // * an account permission
    // * a facility permission matching
    public hasPermissionInAnyFacility(action: PermissionAction): boolean {
        return this.permissions.grants
            .filter(perm => this.isPermissionInAnyFacilityScope(perm))
            .some(perm => this.isPermissionGranted(perm, action, perm.scope.facilityId));
    }

    /**
     * Returns true if there is:
     * * an account permission
     * * no facility specified and any facility permission
     * * facility specified and a matching facility permission
     * * no folder specified and any folder premission
     * * folder specified and a matching folder permission
     */
    public hasAnyPermission(action: PermissionAction, facilityId?: string, folderId?: string): boolean {
        return this.permissions.grants
            .filter(perm => !facilityId || perm.scope.facilityId === facilityId)
            .filter(perm => !folderId || !perm.scope.folderIds?.length || perm.scope.folderIds?.includes(folderId))
            .some(perm => this.isPermissionGranted(perm, action, facilityId ?? perm.scope.facilityId));
    }

    // returns true if there is:
    // * an account permission
    // * facility specified and a matching facility permission
    // * no folder specified and any folder permission
    // * folder specified and a matching folder permission
    public hasFacilityOrAnyFolderPermission(action: PermissionAction, facilityId?: string, folderId?: string): boolean {
        return this.permissions.grants
            .filter(perm => !perm.scope.facilityId || perm.scope.facilityId === facilityId)
            .filter(perm => !folderId || !perm.scope.folderIds?.length || perm.scope.folderIds?.includes(folderId))
            .some(perm => this.isPermissionGranted(perm, action, facilityId ?? perm.scope.facilityId));
    }

    addFolder(id: string, parentId: string) {
        this.permissions.grants.forEach(x => {
            const parent = x.scope.folderIds?.indexOf(parentId);
            if (parent >= 0) {
                x.scope.folderIds.push(id);
            }
        });
    }

    private isPermissionInScope(permission: PermissionApiResponseItem, facilityId?: string, folderId?: string): boolean {
        if (permission.scope.facilityId && facilityId !== permission.scope.facilityId) {
            return false;
        }
        if (permission.scope.folderIds?.length && !permission.scope.folderIds.includes(folderId)) {
            return false;
        }
        return true;
    }

    private isPermissionInAnyFacilityScope(permission: PermissionApiResponseItem): boolean {
        return !permission.scope.folderIds?.length;
    }

    /** Whether `permission` grants `action` and the account is entitled to it at `facilityId` (none means account-wide). */
    private isPermissionGranted(permission: PermissionApiResponseItem, action: PermissionAction, facilityId?: string): boolean {
        if (!this.isEntitled(action, facilityId)) return false;
        return permission.grantedActions.some(grantedAction => this.isActionGranted(action, grantedAction));
    }

    /**
     * Whether the account is entitled to `action` at `facilityId`: the site's product packages decide catalog-gated actions
     * once resolved; otherwise `enabledActions`, letting a site-settings action stand in for a split one, as on the server.
     */
    private isEntitled(action: PermissionAction, facilityId?: string): boolean {
        return isEntitledAction(action, this.permissions.enabledActions, this.siteEntitlements?.(facilityId));
    }

    /** Whether `grantedAction` covers `desiredAction`: it, its site-settings fallback, or Administer Account. */
    private isActionGranted(desiredAction: PermissionAction, grantedAction: PermissionAction): boolean {
        return grantCovers(desiredAction, grantedAction);
    }

    public isGlobalAdmin() {
        return this.permissions.grants.some(grant => grant.groupId === 'global-admin' && !grant.scope.facilityId);
    }

    /** Whether the account is entitled to `action` at `facilityId`, with no grant considered; without a site, any package counts. */
    public isEntitledAt(action: PermissionAction, facilityId?: string): boolean {
        return this.isEntitled(action, facilityId);
    }

    /** Whether the account is entitled to the action: with site entitlements, by any package it holds. */
    hasAccountPermission(action: PermissionAction) {
        return isPackageEntitled(action, this.siteEntitlements?.()) ?? this.permissions.enabledActions.includes(action);
    }

    /** Whether the user may see `person` in the directory or Live Look at `facilityId`; a site whose packages leave the action out refuses it. */
    hasPersonPermission = (action: PermissionAction, person: PersonLike, isPersonOnFacility?: PersonFacilityPredicate, facilityId?: string): boolean =>
        this.hasPersonPermissionMemo(action, person, isPersonOnFacility, facilityId, this.entitlementKeys.key(facilityId ? [facilityId] : [undefined, ...this.grantSites()]));

    private readonly hasPersonPermissionMemo = memoize(
        (action: PermissionAction, person: PersonLike, isPersonOnFacility: PersonFacilityPredicate | undefined, facilityId: string | undefined, _key: string) =>
            this.permissions.grants.some(grant => !isPackageDenied(action, this.siteEntitlements?.(facilityId ?? grant.scope.facilityId))
                && grantCoversPerson(grant, action, person, this.permissions, isPersonOnFacility, facilityId)),
        (action, person, _, facilityId, key) => `${action}:${person.id}:${facilityId}:${key}`,
    );

    /** Every site a grant is scoped to. */
    private grantSites(): string[] {
        return [...new Set(this.permissions.grants.map(grant => grant.scope.facilityId).filter((id): id is string => !!id))];
    }
}
